// src/features/media/useTextTracksWithPending.ts

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { MEDIA_QUERY_KEYS, TEXT_TRACK_PENDING_POLL_MS } from '@/lib/constants/media';
import { pendingTextTracksStore } from './PendingTextTracksStore';
import { TextTrackUtils } from './TextTrackUtils';
import type { ITextTrackData } from '@/lib/types/Media';

const EMPTY_TRACKS: ITextTrackData[] = [];

/**
 * Server text tracks merged with the pending overlay (created-but-not-yet-ready
 * tracks the media view does not return). Prunes overlay entries once the
 * server confirms them and polls the video query while unconfirmed entries
 * remain (ingest normally completes in seconds).
 */
export function useTextTracksWithPending(
  mediaId: number,
  serverTracksInput: ITextTrackData[] | undefined
): ITextTrackData[] {
  const { selectedCId } = useAuth();
  const queryClient = useQueryClient();
  const serverTracks = serverTracksInput ?? EMPTY_TRACKS;

  const pending = useSyncExternalStore(pendingTextTracksStore.subscribe, () =>
    pendingTextTracksStore.getForMedia(mediaId)
  );

  useEffect(() => {
    pendingTextTracksStore.removeConfirmed(mediaId, serverTracks);
  }, [mediaId, serverTracks]);

  const hasPending = pending.length > 0;
  useEffect(() => {
    if (!hasPending || selectedCId == null) return;
    const queryKey = MEDIA_QUERY_KEYS.video(selectedCId, mediaId);
    const intervalId = setInterval(() => {
      // Re-read from the store: aged-out entries must stop the refetches even
      // when identical server responses cause no re-render.
      if (!TextTrackUtils.shouldPoll(pendingTextTracksStore.getForMedia(mediaId), Date.now())) {
        return;
      }
      queryClient.invalidateQueries({ queryKey });
    }, TEXT_TRACK_PENDING_POLL_MS);
    return () => clearInterval(intervalId);
  }, [hasPending, selectedCId, mediaId, queryClient]);

  return useMemo(
    () =>
      TextTrackUtils.mergeWithPending(
        serverTracks,
        pending.map(p => p.track)
      ),
    [serverTracks, pending]
  );
}
